<script setup lang="ts">
import { h, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { RecycleScroller } from 'vue-virtual-scroller'
import { isModifierClick } from '@lib/util/navigation.util'

type Item = { canvas: { id: string; name: string; tags?: string[] }; id: string }

interface Props {
  items: Item[]
  maxItemsToRender?: number
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  maxItemsToRender: 5,
})

const emit = defineEmits<{
  (e: 'delete', item: Item): void
  (e: 'view', item: Item, newTab?: boolean): void
  (e: 'setHome', item: Item): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const SCROLLER_ITEM_SIZE = 44

function deleteItem(item: Item) {
  emit('delete', item)
}

function setHomeItem(item: Item) {
  emit('setHome', item)
}

const displayTitle = computed(
  () => props.title ?? t('canvasUI.components.CTemplateAccessManage.templates', props.items.length),
)

function onView(item: Item, e: MouseEvent) {
  emit('view', item, isModifierClick(e))
}

function isHome(item: Item): boolean {
  return item.canvas.tags?.includes('home') ?? false
}

function getActionsMenu(): DropdownMixedOption[] {
  return [
    {
      label: () => h('div', { class: 'font-size-4' }, t('canvasUI.components.CTemplateAccessManage.setHome')),
      key: 'setHome',
      icon: () => h(CIcon, { icon: 'home' }),
    },
  ]
}

function handleAction(key: string, item: Item) {
  if (key === 'setHome') {
    setHomeItem(item)
  }
}
</script>

<template>
  <CCard bordered size="medium">
    <div>
      <div class="flex items-center justify-between gap-1">
        <div class="flex gap-4 items-center">
          <CIcon icon="presentation-screen" size="14" />

          <div class="flex font-bold">
            {{ displayTitle }}
          </div>
        </div>
      </div>
    </div>
    <RecycleScroller
      v-slot="{ item: item }: { item: Item }"
      :buffer="SCROLLER_ITEM_SIZE * maxItemsToRender * 2"
      class="mt-4 recycle-scroller"
      :itemSize="SCROLLER_ITEM_SIZE"
      :items="items"
      keyField="id"
      :style="items.length > maxItemsToRender ? { maxHeight: `${SCROLLER_ITEM_SIZE * maxItemsToRender}px` } : undefined"
    >
      <div class="flex items-center justify-between gap-1">
        <div class="flex items-center gap-2">
          <CIcon v-if="isHome(item)" :color="themeVars.primary" icon="home" size="medium" />
          <div class="font-bold">{{ item.canvas.name }}</div>
        </div>

        <div class="flex items-center gap-4 mr-2">
          <CButton circle :color="themeVars.error2" @click="deleteItem(item)">
            <template #icon>
              <CIcon class="ml-1" :color="themeVars.error" icon="file-minus" size="medium" />
            </template>
          </CButton>
          <CButton circle @click="onView(item, $event)">
            <template #icon>
              <CIcon icon="eye" size="medium" />
            </template>
          </CButton>
          <CDropdown :options="getActionsMenu()" trigger="click" @select="(key: string) => handleAction(key, item)">
            <CButton circle>
              <template #icon>
                <CIcon icon="ellipsis-vertical" size="medium" />
              </template>
            </CButton>
          </CDropdown>
        </div>
      </div>
    </RecycleScroller>
  </CCard>
</template>
